import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ALL, campaignLabel, inCampaign, useCampaign } from '@lib/campaigns';
import { CampaignScopeNote } from '@components/CampaignScopeNote';
import { QueueTable, useQueueGrid } from '@components/QueueTable';
import { ThreadTable } from '@components/ThreadTable';
import { FilterRail, RailSlicer } from '@components/FilterRail';
import { EventDrawer } from '@components/EventDrawer';
import { MemberView } from '@components/MemberView';
import { openEvent, setOpenMode, useOpenMode } from '@lib/open-mode';
import { cn } from '@/lib/cn';
import { slicerCount } from '@lib/grid';
import { matchAll } from '@lib/care-api';
import { useEventQueue } from '@lib/useEventQueue';
import { sentAwayHides } from '@lib/find-member';
import { MATCH_GROUP_ORDER, MATCH_GROUP_WORDING, SUGGEST_WORDING, eventMatchState, needsConfirmLookup, threadMatchState, type MatchGroup } from '@lib/match-suggest';
import { clientDef, useClient, useFeatures } from '@lib/clients';
import { QueueActionBar, eventBarRow } from '@components/QueueActionBar';
import { UpdateResultGraph, UpdateRunningBar, type UpdateCounts } from '@components/UpdateResultGraph';
import {
  CAMPAIGN_META,
  NO_CAMPAIGN_WORDING,
  STATUS_META,
  campaignKeyOf,
  statusLabel,
  type EventRow,
  type EventSource,
  type EventStatus,
  REPLY_CLASS_META,
  type SmsReplyClass,
  type Totals,
} from '@lib/care-model';

/**
 * InboundQueue - Flow A. Two views over the same response:
 *
 *   - THREAD view (default, William 2026-07-09): one row per member / PCP / phone / email, with
 *     every comm for that person collapsed into it. New activity on an already-worked thread lights
 *     the row amber. Each row carries a suggested action, so a reviewer sees what is being asked for
 *     without opening anything.
 *   - FLAT view: the original one-row-per-event table, kept as a fallback.
 *
 * The server returns `items` (events) and `threads` (the grouping) in ONE response, so the toggle
 * costs no round-trip and cannot split the listCache. Both views fetch every open status and narrow
 * by the Status slicer in the browser (below), so a thread's rollup status and an event's status are
 * judged against the same selection.
 *
 * Threads are a projection. Opening any event, from either view, opens the same EventDrawer, and all
 * per-event workflow (claim / disposition / send to review / the resolution envelope) is unchanged.
 *
 * HIS CARE CONSOLE PAGE, SLICE A (img1 to img6, 24 Sep 2026). Deleted: the "Member Care · Flow A"
 * line (img1), the texting-hours note (img2; the platform still refuses a send outside 8 AM to 9 PM
 * Pacific and the drawer's send box says so on that refusal), the Internal numbers button (img4; the
 * list stays in force, it is only no longer edited here) and the Fax failures button (img5; the
 * queue keeps leaving the fax-failure referrals out, as it did by default). Every filter now sits in
 * ONE rail on the right that slides out and in (img3 the status row, now a multi-select Status
 * slicer; img6 the campaign, source and grid slicers). The rail narrows what is shown and writes
 * nothing; the line saying what the slicers hide stays over the table (row 354).
 */

/**
 * THE STATUS SLICER (his img3: "Make this a slicer on the right side - so can select one or more").
 * The former tabs New / In progress / In review / All: one or more at once, nothing chosen meaning
 * all. Any other status the server counts (an entry recorded as actioned, say) is offered too while it
 * has a count, so no row is unreachable.
 *
 * THE OPENING (William, 16 Oct 2026 8:52 PM: "when unclaimed goes to main queue; when claimed goes in the
 * main queue with the user name"): the page opens on every open status, New, In progress and In review, in
 * both views. A Claim moves a New entry to In progress (the server's rule, unchanged), so opening on New
 * alone would drop a claimed case out of the main queue. Closed and spam stay out of the opening and are
 * reached through the slicer. It opened on New alone, as the tabs did, until then.
 */
/**
 * THE UPDATE BANNER (William, 17 Oct 2026 12:30 AM: "It is trying to match against 26 fobt - NO! It has to match
 * with the client and campaign in the db."; 12:3x AM: "each of the update and match need to be with the client (at
 * the top)"). The server already matched each entry against its own campaign's roster in the selected client's
 * database, the client's own campaign when the entry names none; only these words were wrong. They name the client
 * chosen in the header and the rosters the server says it read, and never a fixed campaign.
 */
export const UPDATE_WORDING = {
  start: (clientName: string, ownCampaign: string | null, selectedCampaign: string | null): string =>
    `Matching open entries for ${clientName} against each entry's own campaign roster${selectedCampaign ? ` (${selectedCampaign})` : ''} in ${clientName}'s database; an entry naming no campaign is matched against ${clientName}'s own campaign${ownCampaign ? ` (${ownCampaign})` : ''}...`,
  /** The rosters read are said once, under the graph (UpdateResultGraph), not in this line (17 Oct 2026). */
  done: (clientName: string, d: UpdateCounts): string => {
    const skipped = d.skipped ? `, ${d.skipped} skipped` : '';
    return `Update complete for ${clientName}: ${d.matched} matched, ${d.ambiguous} ambiguous, ${d.unmatched} unmatched, ${d.suggested ?? 0} suggested${skipped}.`;
  },
  failed: (clientName: string): string => `Update failed for ${clientName}.`,
} as const;

export const STATUS_SLICER_BASE: readonly EventStatus[] = ['new', 'in_progress', 'in_review'];
export const STATUS_OPENING: ReadonlySet<EventStatus> = new Set<EventStatus>(['new', 'in_progress', 'in_review']);

export function statusOptions(totals: Totals | undefined): { key: EventStatus; label: string; count: number | undefined }[] {
  const keys: EventStatus[] = [...STATUS_SLICER_BASE];
  if (totals) {
    for (const k of Object.keys(STATUS_META) as EventStatus[]) {
      if (keys.includes(k)) continue;
      const n = totals[k as keyof Totals];
      if (typeof n === 'number' && n > 0) keys.push(k);
    }
  }
  return keys.map((k) => {
    const n = totals?.[k as keyof Totals];
    return { key: k, label: statusLabel(k), count: typeof n === 'number' ? n : undefined };
  });
}

/** Nothing chosen means every status. */
export function statusPasses(sel: ReadonlySet<EventStatus>, s: EventStatus): boolean {
  return sel.size === 0 || sel.has(s);
}

/**
 * The follow rule: a drawer press that moves the open entry to a status the slicer is not showing
 * ADDS that status to the selection, so the row stays on screen (the tabs used to switch). Returns
 * null when nothing needs to change.
 */
export function followStatus(sel: ReadonlySet<EventStatus>, moved: EventStatus): Set<EventStatus> | null {
  if (statusPasses(sel, moved)) return null;
  const next = new Set(sel);
  next.add(moved);
  return next;
}

/** Row 593 (e): what the default view leaves out; the Bad or blocked number chip asks for them back. */
export const BAD_NUMBER_HIDDEN: readonly EventSource[] = ['out-calling', 'bad-number'];

// 'out-calling' is deliberately absent: the FOBT non-returner worklist is a proactive CALL list, not
// an inbound communication, and it lives on its own /calling page. It is excluded server-side
// (notSource) so it never inflates this page's counts or its payload.
//
// Print Station stays, and its chip counts the 'no_fax_number' referrals that live here. The
// fax-failure half of that source is worked in another portal (feedback #6), so this page always
// asks for excludeFaxFailures (his img5 deleted the button that lifted it).
const SOURCE_CHIPS: { key: EventSource; label: string }[] = [
  { key: 'member', label: 'Member' },
  { key: 'intake', label: 'Intake' },
  { key: 'print-station', label: 'Print Station' },
  { key: 'pcp', label: 'PCP' },
  // Row 593 (e), his R. 136 "yes": the texts the carrier refused (poll_sms_rejections.py) show under
  // THIS chip only, never in the default view, so about 46 a day do not bury the member work.
  { key: 'bad-number', label: 'Bad or blocked number' },
];

const VIEW_STORAGE_KEY = 'membercare.inbound.view';
export const INBOUND_RAIL_KEY = 'membercare.inbound.rail-collapsed';
type ViewMode = 'thread' | 'flat';

function initialView(): ViewMode {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'flat' ? 'flat' : 'thread';
  } catch {
    return 'thread'; // storage blocked (private mode / policy): fall back to the default view
  }
}

function sameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  return a.size === b.size && [...a].every((v) => b.has(v));
}

export function InboundQueue() {
  // The plan whose queue this is. Read here only so an EMPTY queue can name it (see emptyLabel
  // below); every request already carries the client through care-api.
  const client = useClient();
  const [view, setViewState] = useState<ViewMode>(initialView);
  const [statusSel, setStatusSel] = useState<Set<EventStatus>>(() => new Set(STATUS_OPENING));
  /** PART L: cases sent to the Approval Queue from this page, by id, with when they were sent. */
  const [sentAway, setSentAway] = useState<Map<string, number>>(() => new Map());
  /**
   * ONE CLICK (William, 22 Oct 2026: "then it should immediately go off from this list and go to approvals"): the rows a
   * one-click press took off the list at the click (an event id in the flat view, a thread key in the grouped view),
   * until the press ends. Then a row sent stays hidden by sentAway; a row not sent comes back, ticked.
   */
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set());
  /** Source, one or more (his img6); nothing chosen means every source the default view shows. */
  const [sourceSel, setSourceSel] = useState<Set<EventSource>>(new Set());
  /**
   * CAMPAIGN FILTER, multi-select, EMPTY MEANS ALL (William 2026-09-14). A Set rather than a single
   * key because a plan runs several campaigns at once and the operator's real question is "show me
   * both A1c-KED campaigns". The token 'none' is a member of the set like any slug, so "show me only
   * what is not campaign-linked" is one click rather than a separate mode; that bucket is about a
   * third of the queue and permanent.
   */
  const [campaignSel, setCampaignSel] = useState<Set<string>>(new Set());
  /** Row 232: the text-reply classes, multi-select; empty means no opinion. Flat view only. */
  const [replySel, setReplySel] = useState<Set<SmsReplyClass>>(new Set());
  /**
   * PART R (William, 17 Oct 2026 12:32 AM: "Also a flag "confirm lookup""): the rows with a suggested member nobody
   * has confirmed yet. Empty = every row, as the other slicers.
   */
  const [lookupSel, setLookupSel] = useState<Set<'confirm'>>(new Set());
  /**
   * THE MATCH SLICER (William, 17 Oct 2026 about 12:50 AM: "so I can isolate them at once"): one or more of the Match
   * column's six groups. A page filter like Lookup, in both views, so it isolates a group in one click; a grid slicer
   * would keep every row with no campaign on screen (row 354), and most unmatched calls name none.
   */
  const [matchSel, setMatchSel] = useState<Set<MatchGroup>>(new Set());
  /**
   * I3 (William, 16 Oct 2026 about 10 PM: remove): row 391's reply GROUP buttons (Opt In, Opt Out,
   * Help, All other) and their presses are gone from the flat view. The five buttons on top
   * (QueueActionBar) are the presses now, and every row of the flat view carries their tick box. With
   * MEMBERCARE_REPLY_BUTTONS off the row 232 reply chips stay in the rail, as before.
   */
  const features = useFeatures();
  const replyButtons = features.replyButtons;
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  /**
   * 16 Oct 2026 (his word: "Like the print - put a checkmark next to each name"): the grouped view's tick
   * boxes, by thread key, for the five buttons on top. A row the filters or slicers hide is unticked, so a
   * press never reaches a row she cannot see.
   */
  const [threadChecked, setThreadChecked] = useState<Set<string>>(new Set());
  const [notSentLines, setNotSentLines] = useState<string[]>([]);
  const threadSelection = useMemo(() => ({
    checked: threadChecked,
    onToggle: (key: string) => setThreadChecked((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    }),
    onToggleAll: (shown: string[]) => setThreadChecked((prev) => (
      shown.length > 0 && shown.every((k) => prev.has(k)) ? new Set<string>() : new Set(shown)
    )),
    onShown: (shown: string[]) => setThreadChecked((prev) => {
      if (prev.size === 0) return prev;
      const on = new Set(shown);
      const kept = [...prev].filter((k) => on.has(k));
      return kept.length === prev.size ? prev : new Set(kept);
    }),
  }), [threadChecked]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  // William, 2 Oct 2026: every case opens the way the top bar's switch says. His three choices ("yes -
  // give her three choices as a default presentation"): Under the row (Slice B's member view, his img7,
  // on the GROUPED view only, below), the side panel (this page's drawer), or a pop out (its own window,
  // nothing selected here). The old per-page MEMBER_BELOW_KEY ('membercare.inbound.memberView') is
  // ignored: the switch's one remembered choice is the only one read.
  const openMode = useOpenMode();
  const openCase = (id: string) => openEvent(id, setSelected);
  const [banner, setBanner] = useState('');
  const [matching, setMatching] = useState(false);
  /** The Update's picture: the moving bar while it runs, then the result graph while its banner stands. */
  const [updateRun, setUpdateRun] = useState<{ clientName: string; startedAt: number } | null>(null);
  const [updateResult, setUpdateResult] = useState<{ text: string; clientName: string; data: UpdateCounts } | null>(null);
  // The rail's slicer box: the grid draws its slicers into it (CareGrid slicerHost). A state ref, so
  // the tables re-render once it exists; null while the rail is slid in.
  const [slicerHost, setSlicerHost] = useState<HTMLDivElement | null>(null);
  const [threadSlicers, setThreadSlicers] = useState(0);
  const [resetKey, setResetKey] = useState(0);

  const setView = (v: ViewMode) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      /* storage blocked; the choice just will not persist across reloads */
    }
  };

  const threadMode = view === 'thread';
  /**
   * Under the row opens a member under its row only where there is a row of members to open under: the
   * grouped view. The flat view has none, so there 'below' is exactly the side panel (openEvent selects,
   * the drawer opens).
   */
  const memberBelow = threadMode && openMode === 'below';
  const statusPass = (s: EventStatus): boolean => statusPasses(statusSel, s);

  /**
   * AN EMPTY QUEUE MUST NAME THE PLAN IT IS EMPTY FOR (2026-09-06, from the Workflow Hub's code read).
   * An operator stranded on the wrong plan (the picker's choice persists) would otherwise see an
   * empty day's work and a sentence that does not mention the plan. The fix is the message, not the
   * mechanism: naming the plan costs one sentence and closes the misreading.
   */
  const emptyLabel = (noun: string): string =>
    `No ${noun} in this view for ${clientDef(client).displayName}.`;

  const { data, loading, reload } = useEventQueue({
    direction: 'inbound',
    // Every open status, both views: the Status slicer narrows in the browser (one or more at once).
    status: undefined,
    q,
    notSource: sourceSel.has('bad-number') ? 'out-calling' : BAD_NUMBER_HIDDEN,
    excludeFaxFailures: true,
    groupBy: threadMode ? 'thread' : undefined,
  });

  /**
   * The campaign chips are DERIVED FROM THE ROWS, never a hardcoded list, so a campaign registered on
   * the portal appears here the day its first event lands. The universe is the STATUS- and
   * SOURCE-filtered but CAMPAIGN-unfiltered set, so selecting one campaign does not make the other
   * chips disappear from under the operator's cursor. 'none' always sits last.
   */
  // ROW 354: the top-bar campaign scopes the universe FIRST, and his chips (14 Sep) work inside it.
  // A row naming no campaign always passes (inCampaign), so member-started work never drops out.
  const topCampaign = useCampaign();
  const sourcePass = (s: EventSource): boolean => sourceSel.size === 0 || sourceSel.has(s);
  // PART L (William, 16 Oct 2026 about 11:24 PM: "at that time the window closes and the row disappears"): a case
  // sent with Send to Approval Queue leaves this list at once (removed here), and stays gone once the server's
  // list confirms it is in review; a case returned to the queue (in progress again) comes back.
  const statusScoped = (data?.items ?? []).filter((r) => (threadMode || statusPass(r.status)) && !sentAwayHides(sentAway, r) && !leaving.has(r.eventId));
  const sourced = statusScoped.filter((r) => sourcePass(r.source));
  const setAside = sourced.filter((r) => !inCampaign(r.campaign, topCampaign)).length;
  const campaignBase = sourced.filter((r) => inCampaign(r.campaign, topCampaign));
  const campaignCounts = new Map<string, number>();
  for (const r of campaignBase) {
    const k = campaignKeyOf(r);
    campaignCounts.set(k, (campaignCounts.get(k) ?? 0) + 1);
  }
  const campaignChips = Array.from(campaignCounts.entries())
    .map(([key, count]) => ({
      key,
      count,
      label: key === 'none' ? NO_CAMPAIGN_WORDING.chip : (CAMPAIGN_META[key]?.label ?? key),
    }))
    .sort((a, b) => {
      if (a.key === 'none') return 1;
      if (b.key === 'none') return -1;
      return a.label.localeCompare(b.label, 'en-US', { sensitivity: 'base', numeric: true });
    });

  const campaignPass = (key: string): boolean => campaignSel.size === 0 || campaignSel.has(key);

  // Row 232: reply-class chips, DERIVED FROM THE ROWS like the campaign chips, so none shows until a
  // feed reply exists. Selecting one narrows to replies of that class; empty means every row.
  const replyCounts = new Map<SmsReplyClass, number>();
  for (const r of campaignBase) {
    if (r.replyClass) replyCounts.set(r.replyClass, (replyCounts.get(r.replyClass) ?? 0) + 1);
  }
  const REPLY_ORDER: SmsReplyClass[] = ['YES_KIT', 'DECLINE_CAMPAIGN', 'STOP', 'HELP', 'OTHER'];
  const replyChips = REPLY_ORDER.filter((k) => replyCounts.has(k)).map((k) => ({ key: k, count: replyCounts.get(k) ?? 0 }));
  const replyPass = (k: SmsReplyClass | '' | undefined): boolean => replySel.size === 0 || (!!k && replySel.has(k));

  // I3 (16 Oct 2026): no reply group narrows the flat view any more; with the switch off the row 232 chips do.
  const lookupPass = (r: EventRow): boolean => lookupSel.size === 0 || needsConfirmLookup(r);
  const flatBeforeMatch = statusScoped.filter(
    (r) => sourcePass(r.source) && inCampaign(r.campaign, topCampaign) && campaignPass(campaignKeyOf(r))
      && (replyButtons || replyPass(r.replyClass)),
  ).filter(lookupPass);
  const items = matchSel.size === 0 ? flatBeforeMatch : flatBeforeMatch.filter((r) => matchSel.has(eventMatchState(r).group));

  const showHandledBy = !sameSet(statusSel, STATUS_OPENING);

  // Row 356: the flat view through the shared grid. Server order (newest first) until she sorts; the
  // page's own slicers keep status, source, campaign and text replies, so the grid slices the other
  // fields; and a contact with no campaign is never hidden by a slicer (row 354: a member started it).
  // Approval 185: with the reply buttons on, the Cohort column and its slicer too, so she can sort and
  // slice the yeses by kit; with the switch off neither exists. I4 (William, 16 Oct 2026 about 10 PM:
  // remove): the Offer slicer is gone; the Offer column went at 8:20 PM.
  // His img3: status is the page's own Status slicer now, so the grid does not offer a second one.
  // William, 16 Oct 2026 8:20 PM: "Make sure the 'flat events' screen looks the same as the group by member".
  // The flat view draws its rows like the grouped view (QueueTable rowsLook) with the tick box on every row,
  // for the same five buttons on top. The header box ticks every row on screen.
  const toggleAllShown = useCallback((ids: string[]) => setChecked((prev) => (
    ids.length > 0 && ids.every((id) => prev.has(id)) ? new Set<string>() : new Set([...prev, ...ids])
  )), []);
  const flatGrid = useQueueGrid({
    table: 'inbound-flat',
    items,
    slicers: replyButtons
      // 17 Oct 2026 about 12:50 AM: the three-way Member slicer gave way to the rail's Match slicer (six groups).
      ? ['channel', 'instruction', 'claimedBy', 'cohort', 'cycle']
      : ['channel', 'instruction', 'claimedBy'],
    kitColumns: replyButtons,
    protectNoCampaign: true,
    showHandledBy,
    // 16 Oct 2026 8:20 PM: a tick box on every row, as in the grouped view, for the five buttons on top.
    selectable: true,
    rowsLook: true,
    onToggleAll: toggleAllShown,
    checkedIds: checked,
    onToggleCheck: toggleCheck,
  });
  const visibleRows = flatGrid.rows;

  // A ticked row a filter or a slicer then hides is UNTICKED: a press must never reach a row she cannot see.
  useEffect(() => {
    setChecked((prev) => {
      if (prev.size === 0) return prev;
      const shown = new Set(visibleRows.map((r) => r.eventId));
      const kept = [...prev].filter((id) => shown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [visibleRows]);

  // The expanded thread rows render their events from the same payload; no per-thread fetch.
  const eventsById = useMemo(
    () => new Map((data?.items ?? []).map((r) => [r.eventId, r])),
    [data?.items],
  );
  /** PART R: a member's row needs its lookup confirmed when any of its entries does. */
  const threadNeedsLookup = (t: { eventIds: string[] }): boolean => t.eventIds.some((id) => {
    const r = eventsById.get(id);
    return !!r && needsConfirmLookup(r);
  });

  /**
   * PART S2 (William, 17 Oct 2026 12:53 AM: "And then it would send all for approval"): a member's row whose cases
   * the bar's Send to Approval Queue sent leaves the list, by the same rule as the drawer's send (PART L).
   */
  const threadSentAway = (t: { eventIds: string[] }): boolean => t.eventIds.some((id) => {
    const r = eventsById.get(id);
    return sentAway.has(id) && (r ? sentAwayHides(sentAway, r) : true);
  });
  const onSentToApproval = (ids: string[]) => setSentAway((prev) => {
    const next = new Map(prev);
    for (const id of ids) next.set(id, Date.now());
    return next;
  });
  /** ONE CLICK: take the pressed rows off the list now and clear their ticks; at the end, hand them back. */
  const leaveHandlers = (setTicks: typeof setChecked) => ({
    onLeaving: (keys: string[]) => {
      setLeaving((prev) => new Set([...prev, ...keys]));
      setTicks((prev) => new Set([...prev].filter((k) => !keys.includes(k))));
    },
    onReturned: (keys: string[], tickAgain: string[]) => {
      setLeaving((prev) => new Set([...prev].filter((k) => !keys.includes(k))));
      if (tickAgain.length) setTicks((prev) => new Set([...prev, ...tickAgain]));
    },
  });

  // A thread matches a source chip when it HOLDS an event of that origin. Its newest event's origin
  // would be the wrong test: a referral thread would stop matching the referral chip the moment the
  // member texted, and the chip is the only way back to a row this page excludes by default.
  const threadsBeforeMatch = (data?.threads ?? []).filter(
    (t) =>
      statusPass(t.status)
      && (sourceSel.size === 0 || t.sources.some((s) => sourceSel.has(s)))
      // Row 354: a thread holding no campaign-bearing event always passes; one holding the chosen campaign passes.
      && (!t.campaigns.length || t.campaigns.some((c) => inCampaign(c, topCampaign)))
      // A thread matches a campaign chip when it HOLDS that campaign, the same "holds" rule as the
      // source chip above. A thread holding no campaign-bearing event matches 'none', so the two
      // views agree about which rows are unattributed.
      && (campaignSel.size === 0
        || (t.campaigns.length
          ? t.campaigns.some((c) => campaignSel.has(c))
          : campaignSel.has('none')))
      && (lookupSel.size === 0 || threadNeedsLookup(t))
      && !threadSentAway(t)
      && !leaving.has(t.threadKey),
  );
  const threads = matchSel.size === 0
    ? threadsBeforeMatch
    : threadsBeforeMatch.filter((t) => matchSel.has(threadMatchState(t, eventsById).group));
  /** The Match slicer's counts: the rows every OTHER filter leaves, by group (a button never reads 0 because a sibling is chosen). */
  const matchCounts = new Map<MatchGroup, number>();
  if (threadMode) for (const t of threadsBeforeMatch) { const g = threadMatchState(t, eventsById).group; matchCounts.set(g, (matchCounts.get(g) ?? 0) + 1); }
  else for (const r of flatBeforeMatch) { const g = eventMatchState(r).group; matchCounts.set(g, (matchCounts.get(g) ?? 0) + 1); }
  const matchOptions = MATCH_GROUP_ORDER.map((g) => ({ key: g, label: MATCH_GROUP_WORDING.labels[g], count: matchCounts.get(g) ?? 0 }));
  const lookupCount = threadMode
    ? (data?.threads ?? []).filter((t) => statusPass(t.status) && threadNeedsLookup(t)).length
    : campaignBase.filter((r) => needsConfirmLookup(r)).length;

  const activeTotals = threadMode ? data?.threadTotals : data?.totals;
  const bySource = activeTotals?.bySource;
  const sourceCount = (k: EventSource): number | undefined => {
    if (!bySource) return undefined;
    if (k === 'print-station') return bySource.printStation;
    if (k === 'pcp') return bySource.pcp;
    if (k === 'member') return bySource.member;
    if (k === 'intake') return bySource.intake;
    if (k === 'bad-number') return undefined;
    return bySource.portal;
  };

  // THE RAIL'S COUNT AND RESET: every slicer with a value chosen, the page's and the table's. Reset
  // puts the page back as it opens (Status New, nothing else) and clears the table's slicers.
  const gridSlicers = threadMode ? threadSlicers : slicerCount(flatGrid.view.slicers);
  const railActive =
    (statusSel.size > 0 ? 1 : 0)
    + (sourceSel.size > 0 ? 1 : 0)
    + (campaignSel.size > 0 ? 1 : 0)
    + (lookupSel.size > 0 ? 1 : 0)
    + (matchSel.size > 0 ? 1 : 0)
    + (!threadMode && !replyButtons && replySel.size > 0 ? 1 : 0)
    + gridSlicers;
  const railAtOpening =
    sameSet(statusSel, STATUS_OPENING) && sourceSel.size === 0 && campaignSel.size === 0 && replySel.size === 0 && lookupSel.size === 0 && matchSel.size === 0 && gridSlicers === 0;
  const clearFlatSlicers = flatGrid.view.clearSlicers;
  const resetRail = () => {
    setStatusSel(new Set(STATUS_OPENING));
    setSourceSel(new Set());
    setCampaignSel(new Set());
    setReplySel(new Set());
    setLookupSel(new Set());
    setMatchSel(new Set());
    clearFlatSlicers();
    setResetKey((k) => k + 1);
  };
  const shownCount = threadMode ? threads.length : items.length;
  const totalCount = threadMode ? (data?.threads ?? []).length : (data?.items ?? []).length;
  const noun = threadMode ? 'threads' : 'events';

  const runMatchAll = async () => {
    setMatching(true);
    // The client chosen in the header (matchAll sends the same one through currentClient()).
    const def = clientDef(client);
    const own = def.referenceCampaign ? campaignLabel(def.referenceCampaign) : null;
    setBanner(UPDATE_WORDING.start(def.displayName, own, topCampaign !== ALL ? campaignLabel(topCampaign) : null));
    setUpdateResult(null);
    setUpdateRun({ clientName: def.displayName, startedAt: Date.now() });
    const r = await matchAll().finally(() => {
      setMatching(false);
      setUpdateRun(null);
    });
    if (r.ok) {
      const data = r.data as UpdateCounts;
      const text = UPDATE_WORDING.done(def.displayName, data);
      setBanner(text);
      setUpdateResult({ text, clientName: def.displayName, data });
    } else {
      setBanner(UPDATE_WORDING.failed(def.displayName));
    }
    reload();
  };

  /** View follows the work: when a drawer mutation moved the selected event into a status the
   * Status slicer is not showing (e.g. Claim: new -> in_progress), that status is ADDED to the
   * selection so the row stays visible (Handled by shows the operator) instead of silently
   * vanishing. Latest-value refs: the drawer's mutate captures this callback at click time, and the
   * round-trip can land AFTER the operator changed the slicer or the event, so the guards read the
   * CURRENT selection, not the click-time closure.
   *
   * Thread mode does NOT follow: an event's new status is not the thread's rollup status, so
   * following the event would widen the view for a thread that has not moved. The drawer is keyed
   * on the selected eventId and stays open regardless, so nothing is lost. */
  const statusRef = useRef(statusSel);
  statusRef.current = statusSel;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onDrawerChanged = (updated?: { eventId: string; status: string; sentToApproval?: boolean }) => {
    reload();
    if (updated?.sentToApproval) {
      // PART L: the row disappears and the view does not follow it into In review.
      setSentAway((prev) => new Map(prev).set(updated.eventId, Date.now()));
      return;
    }
    if (threadMode) return;
    if (!updated || updated.eventId !== selectedRef.current) return;
    if (!(updated.status in STATUS_META)) return;
    const moved = updated.status as EventStatus;
    const next = followStatus(statusRef.current, moved);
    if (!next) return;
    setStatusSel(next);
    setBanner(`Moved to ${statusLabel(moved)} - added to the Status filter so it stays on screen.`);
  };

  return (
    <div className="page-pad page-wide">
      <header>
        <h1 className="section-title">
          Inbound <em>queue</em>
        </h1>
        <div className="section-rule" />
        {/* His word, 16 Oct 2026: the page's description line is removed ("Remove this."). */}
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="text-input"
          type="search"
          aria-label="Search inbound events"
          style={{ maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search phone / email / name / member ID / text"
        />
        <button type="button" className="btn btn-teal" disabled={matching} onClick={() => void runMatchAll()}>
          Update (match all)
        </button>
        <span className="micro muted" style={{ marginLeft: 'auto' }}>
          View
        </span>
        <button
          type="button"
          className={cn('chip', threadMode && 'selected')}
          onClick={() => setView('thread')}
          title="One row per member, PCP, or contact"
        >
          Group by member
        </button>
        <button
          type="button"
          className={cn('chip', !threadMode && 'selected')}
          onClick={() => setView('flat')}
          title="One row per event (the original view)"
        >
          Flat events
        </button>
      </div>

      <div className="queue-layout">
        <div className="queue-main">
          {banner && (
            <div className="note" style={{ marginBottom: 14 }} data-testid="queue-banner">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ flex: 1 }}>{banner}</span>
                {!updateRun && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setBanner(''); setUpdateResult(null); }}>
                    Dismiss
                  </button>
                )}
              </div>
              {updateRun && <UpdateRunningBar clientName={updateRun.clientName} startedAt={updateRun.startedAt} />}
              {/* The graph belongs to its own banner: any later message on this line takes it away. */}
              {!updateRun && updateResult && updateResult.text === banner && (
                <UpdateResultGraph clientName={updateResult.clientName} data={updateResult.data} />
              )}
            </div>
          )}
          {notSentLines.length > 0 && (
            <div className="note warn" style={{ marginBottom: 14 }} data-testid="queue-not-sent">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {notSentLines.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
          )}

          {!threadMode && (
            <QueueActionBar
              ticked={visibleRows.filter((r) => checked.has(r.eventId)).map(eventBarRow)}
              eventsById={eventsById}
              onClear={() => setChecked(new Set())}
              onSentToApproval={onSentToApproval}
              {...leaveHandlers(setChecked)}
              onPressed={(sentence, sentKeys, notSent) => {
                setBanner(sentence);
                setNotSentLines(notSent);
                setChecked((prev) => new Set([...prev].filter((id) => !sentKeys.includes(id))));
                reload();
              }}
            />
          )}
          {threadMode && (
            <QueueActionBar
              ticked={threads.filter((t) => threadChecked.has(t.threadKey))}
              eventsById={eventsById}
              onClear={() => setThreadChecked(new Set())}
              onSentToApproval={onSentToApproval}
              {...leaveHandlers(setThreadChecked)}
              onPressed={(sentence, sentKeys, notSent) => {
                setBanner(sentence);
                setNotSentLines(notSent);
                setThreadChecked((prev) => new Set([...prev].filter((k) => !sentKeys.includes(k))));
                reload();
              }}
            />
          )}

          {threadMode ? (
            <ThreadTable
              selection={threadSelection}
              threads={threads}
              eventsById={eventsById}
              selectedEventId={selected}
              loading={loading}
              onSelectEvent={openCase}
              emptyLabel={emptyLabel('threads')}
              showHandledBy={showHandledBy}
              slicerHost={slicerHost}
              resetSlicersKey={resetKey}
              onSlicerCount={setThreadSlicers}
              openBelow={memberBelow}
              onCloseBelow={() => setSelected(null)}
              renderBelow={(t, id) => (
                <MemberView
                  thread={t}
                  events={t.eventIds.map((e) => eventsById.get(e)).filter((e): e is EventRow => !!e)}
                  selectedEventId={id}
                  onSelectEvent={setSelected}
                  onClose={() => setSelected(null)}
                  onChanged={onDrawerChanged}
                  // "Open in side panel" moves the top bar's switch to the side panel (remembered, like a
                  // click on its chip); the chosen entry stays selected, so the drawer opens on it.
                  onOpenSide={() => setOpenMode('side')}
                />
              )}
            />
          ) : (
            <QueueTable
              grid={flatGrid}
              selectedId={selected}
              loading={loading}
              onSelect={openCase}
              emptyLabel={emptyLabel('inbound events')}
              slicerHost={slicerHost}
            />
          )}
        </div>

        <FilterRail
          storageKey={INBOUND_RAIL_KEY}
          activeCount={railActive}
          onReset={resetRail}
          canReset={!railAtOpening}
          scope={
            <>
              <span data-testid="rail-shown">
                {shownCount.toLocaleString()} of {totalCount.toLocaleString()} {noun}
              </span>
              <CampaignScopeNote others={setAside} />
            </>
          }
        >
          <RailSlicer
            label="Status"
            options={statusOptions(activeTotals)}
            selected={statusSel}
            onChange={setStatusSel}
          />
          <RailSlicer
            label="Campaign"
            options={campaignChips}
            selected={campaignSel}
            onChange={setCampaignSel}
            titleFor={(k) => (k === 'none' ? NO_CAMPAIGN_WORDING.tooltip : undefined)}
          />
          <RailSlicer
            label={SUGGEST_WORDING.filterLabel}
            options={[{ key: 'confirm' as const, label: SUGGEST_WORDING.filter, count: lookupCount }]}
            selected={lookupSel}
            onChange={setLookupSel}
            titleFor={() => SUGGEST_WORDING.filterHelp}
          />
          <RailSlicer
            label={MATCH_GROUP_WORDING.slicer}
            options={matchOptions}
            selected={matchSel}
            onChange={setMatchSel}
          />
          <RailSlicer
            label="Source"
            options={SOURCE_CHIPS.map((c) => ({ ...c, count: sourceCount(c.key) }))}
            selected={sourceSel}
            onChange={setSourceSel}
          />
          {!threadMode && !replyButtons && replyChips.length > 0 && (
            <RailSlicer
              label="Text replies"
              options={replyChips.map((c) => ({ key: c.key, label: REPLY_CLASS_META[c.key].label, count: c.count }))}
              selected={replySel}
              onChange={setReplySel}
              titleFor={(k) => REPLY_CLASS_META[k].help}
            />
          )}
          <div ref={setSlicerHost} data-testid="rail-grid-slicers" />
        </FilterRail>
      </div>

      {selected && !memberBelow && (
        <EventDrawer
          eventId={selected}
          hasAudioHint={eventsById.get(selected)?.hasAudio ?? false}
          onClose={() => setSelected(null)}
          onChanged={onDrawerChanged}
        />
      )}
    </div>
  );
}
